import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  FileDownload as CsvIcon,
  ContentPaste as CopyIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  AccountTree as TreeIcon,
  ErrorOutline as ErrorIcon,
  RestartAlt as ResetIcon,
  CloudDone as CloudDoneIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DashboardLayout from '../components/DashboardLayout';
import { ProcessConfigModal } from '../components/report/ProcessConfigModal';
import { useAppDispatch } from '../store';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import {
  BaseDataRecord,
  AttendanceInfoRecord,
  ProcessNode,
} from '../types/report';
import {
  flattenReportDataToCsv,
  downloadCsvFile,
  copyTextToClipboard,
} from '../utils/csvExporter';

interface TreeNodeWithDepth {
  node: ProcessNode;
  depth: number;
}

export const ReportDataSource: React.FC = () => {
  const dispatch = useAppDispatch();

  // Date Range state
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs().endOf('month'));

  // Pipeline tree state loaded from backend DynamoDB
  const [pipelineTree, setPipelineTree] = useState<ProcessNode[]>([]);
  const [isTreeLoading, setIsTreeLoading] = useState<boolean>(true);
  const [isTreeSaving, setIsTreeSaving] = useState<boolean>(false);

  // Active / Selected Node
  const [selectedNodeId, setSelectedNodeId] = useState<string>('base-data');

  // Execution state & Results
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<BaseDataRecord[] | null>(null);
  const [lastExecutedNodeId, setLastExecutedNodeId] = useState<string | null>(null);
  const [executionTimeMs, setExecutionTimeMs] = useState<number | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);

  // Search & Pagination in Autopsy table
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalNode, setModalNode] = useState<ProcessNode | null>(null);
  const [modalParentId, setModalParentId] = useState<string>('base-data');
  const [modalParentName, setModalParentName] = useState<string>('Base Data');

  // Load pipeline hierarchy from backend DynamoDB on mount
  useEffect(() => {
    let isMounted = true;
    const loadPipelineTree = async () => {
      try {
        setIsTreeLoading(true);
        const res = await apiService.getReportPipelineTree();
        if (isMounted && res.data) {
          setPipelineTree(res.data);
        }
      } catch (err: any) {
        console.error('Failed to load report pipeline tree from backend DynamoDB:', err);
      } finally {
        if (isMounted) setIsTreeLoading(false);
      }
    };

    loadPipelineTree();
    return () => {
      isMounted = false;
    };
  }, []);

  // Helper to persist pipeline tree mutations directly to backend DynamoDB
  const persistTreeToBackend = async (newTree: ProcessNode[], successMsg?: string) => {
    setPipelineTree(newTree);
    setIsTreeSaving(true);
    try {
      await apiService.saveReportPipelineTree(newTree);
      if (successMsg) {
        dispatch(showNotification({ message: successMsg, severity: 'success' }));
      }
    } catch (err: any) {
      console.error('Failed to save pipeline tree to backend DynamoDB:', err);
      dispatch(showNotification({ message: 'Failed to persist pipeline tree to backend DynamoDB', severity: 'error' }));
    } finally {
      setIsTreeSaving(false);
    }
  };

  // Flattened tree ordered by hierarchy
  const orderedTreeNodes = useMemo(() => {
    const result: TreeNodeWithDepth[] = [];

    const appendChildren = (parentId: string, currentDepth: number) => {
      const children = pipelineTree.filter((n) => n.parentId === parentId);
      for (const child of children) {
        result.push({ node: child, depth: currentDepth });
        appendChildren(child.id, currentDepth + 1);
      }
    };

    appendChildren('base-data', 1);
    return result;
  }, [pipelineTree]);

  // Execute pipeline for a given node
  const handleExecute = useCallback(
    async (targetId: string) => {
      if (!fromDate || !toDate) {
        dispatch(showNotification({ message: 'Please select both From Date and To Date', severity: 'warning' }));
        return;
      }

      setLoading(true);
      setExecutionError(null);
      setSelectedNodeId(targetId);
      const startTime = performance.now();

      try {
        const payload = {
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
          targetProcessId: targetId === 'base-data' ? '' : targetId,
          pipelineTree: pipelineTree,
        };

        const res = await apiService.executeReportDataSource(payload);
        const elapsed = Math.round(performance.now() - startTime);

        setData(res.data || []);
        setLastExecutedNodeId(targetId);
        setExecutionTimeMs(elapsed);
        setPage(0);
        setExpandedRowIds({});

        dispatch(
          showNotification({
            message: `Pipeline executed successfully (${res.data?.length || 0} employees returned in ${elapsed}ms)`,
            severity: 'success',
          })
        );
      } catch (err: any) {
        console.error('Pipeline execution error:', err);
        const errMsg = err?.message || err?.response?.data?.message || err?.data?.message || 'Failed to execute pipeline';
        setExecutionError(errMsg);
        dispatch(showNotification({ message: errMsg, severity: 'error' }));
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, pipelineTree, dispatch]
  );

  // Modal actions
  const handleOpenFork = (pId: string, pName: string) => {
    setModalNode(null);
    setModalParentId(pId);
    setModalParentName(pName);
    setModalOpen(true);
  };

  const handleOpenEdit = (node: ProcessNode) => {
    setModalNode(node);
    const parent = pipelineTree.find((n) => n.id === node.parentId);
    setModalParentId(node.parentId);
    setModalParentName(parent ? parent.name : 'Base Data');
    setModalOpen(true);
  };

  const handleCloneNode = (node: ProcessNode) => {
    const clone: ProcessNode = {
      ...node,
      id: 'proc_' + Date.now().toString(36),
      name: `${node.name} (Copy)`,
    };
    const updated = [...pipelineTree, clone];
    persistTreeToBackend(updated, `Cloned process "${node.name}" (saved to backend DynamoDB)`);
  };

  const handleDeleteNode = (id: string, name: string) => {
    // Recursively collect all descendant IDs
    const toDelete = new Set<string>([id]);
    const findChildren = (parentId: string) => {
      pipelineTree
        .filter((n) => n.parentId === parentId)
        .forEach((child) => {
          toDelete.add(child.id);
          findChildren(child.id);
        });
    };
    findChildren(id);

    const updated = pipelineTree.filter((n) => !toDelete.has(n.id));
    if (selectedNodeId && toDelete.has(selectedNodeId)) {
      setSelectedNodeId('base-data');
    }
    persistTreeToBackend(updated, `Deleted process "${name}" and child branches (saved to backend DynamoDB)`);
  };

  const handleSaveModalNode = (savedNode: ProcessNode) => {
    const idx = pipelineTree.findIndex((n) => n.id === savedNode.id);
    let updated: ProcessNode[];
    if (idx >= 0) {
      updated = [...pipelineTree];
      updated[idx] = savedNode;
    } else {
      updated = [...pipelineTree, savedNode];
    }
    setModalOpen(false);
    setSelectedNodeId(savedNode.id);
    persistTreeToBackend(updated, `Saved process "${savedNode.name}" to backend DynamoDB`);
  };

  const handleResetTree = () => {
    if (window.confirm('Reset all process branches back to canonical Base Data in backend DynamoDB?')) {
      setSelectedNodeId('base-data');
      setData(null);
      persistTreeToBackend([], 'Pipeline tree reset in backend DynamoDB');
    }
  };

  // Autopsy Table filtering
  const filteredRecords = useMemo(() => {
    if (!data) return [];
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter((emp) => {
      const idMatch = emp.internalId?.toLowerCase().includes(query);
      const nameMatch = emp.name?.toLowerCase().includes(query);
      return idMatch || nameMatch;
    });
  }, [data, searchQuery]);

  // Discover dynamic Main columns (excluding base keys)
  const dynamicMainCols = useMemo(() => {
    if (!data || data.length === 0) return [];
    const baseKeys = new Set(['internalId', 'employeeUUID', 'name', 'joiningDate', 'resignationDate', 'attandanceInfo']);
    const cols = new Set<string>();
    for (const emp of data) {
      for (const k of Object.keys(emp)) {
        if (!baseKeys.has(k)) cols.add(k);
      }
    }
    return Array.from(cols);
  }, [data]);

  // Discover dynamic Details columns (excluding base keys)
  const dynamicDetailCols = useMemo(() => {
    if (!data || data.length === 0) return [];
    const baseKeys = new Set([
      'Date',
      'inTime',
      'outTime',
      'isLate',
      'LateMinute',
      'isAbsence',
      'isOnLeave',
      'LeaveType',
      'PunchError',
      'isPresentInHoliday',
      'isHoliday',
      'isWeeklyOff',
      'shiftId',
      'shiftName',
      'workStartTime',
      'workEndTime',
      'shiftStartTime',
      'shiftEndTime',
      'graceMinutes',
    ]);
    const cols = new Set<string>();
    for (const emp of data) {
      if (emp.attandanceInfo) {
        for (const day of emp.attandanceInfo) {
          for (const k of Object.keys(day)) {
            if (!baseKeys.has(k)) cols.add(k);
          }
        }
      }
    }
    return Array.from(cols);
  }, [data]);

  // Cell renderer with error trap detection
  const renderCellValue = (val: any) => {
    if (val === null || val === undefined) return <Typography variant="caption" color="text.disabled">—</Typography>;
    if (typeof val === 'boolean') {
      return val ? (
        <Chip label="TRUE" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
      ) : (
        <Chip label="FALSE" size="small" color="default" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
      );
    }

    const str = String(val);
    if (str.startsWith('#ERR:')) {
      return (
        <Tooltip title={`Runtime Calculation Error: ${str}`}>
          <Chip
            icon={<ErrorIcon sx={{ fontSize: '13px !important' }} />}
            label={str}
            size="small"
            color="error"
            sx={{ height: 22, fontSize: '0.72rem', fontFamily: 'monospace' }}
          />
        </Tooltip>
      );
    }

    return <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{str}</Typography>;
  };

  // Export handlers
  const handleExportJson = () => {
    if (!data || data.length === 0) return;
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_data_source_${selectedNodeId}_${dayjs().format('YYYYMMDD_HHmmss')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    if (!data || data.length === 0) return;
    const csvContent = flattenReportDataToCsv(data);
    downloadCsvFile(csvContent, `report_flattened_${selectedNodeId}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    dispatch(showNotification({ message: 'Flattened CSV exported', severity: 'success' }));
  };

  const handleCopyCsv = async () => {
    if (!data || data.length === 0) return;
    const csvContent = flattenReportDataToCsv(data);
    const ok = await copyTextToClipboard(csvContent);
    if (ok) {
      dispatch(showNotification({ message: 'Flattened CSV copied to clipboard', severity: 'success' }));
    } else {
      dispatch(showNotification({ message: 'Failed to copy to clipboard', severity: 'error' }));
    }
  };

  const toggleRowExpanded = (uuid: string) => {
    setExpandedRowIds((prev) => ({ ...prev, [uuid]: !prev[uuid] }));
  };

  const activeNodeName = useMemo(() => {
    if (selectedNodeId === 'base-data') return 'Base Data (Root)';
    const node = pipelineTree.find((n) => n.id === selectedNodeId);
    return node ? node.name : selectedNodeId;
  }, [selectedNodeId, pipelineTree]);

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Top Control Bar: Temporal Span & Execution Header */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  Report Data Source & Pipeline Workspace
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hierarchical DAG engine with canonical Base Data synthesis and real-time autopsy extraction.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(d) => setFromDate(d)}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(d) => setToDate(d)}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                  disabled={loading}
                  onClick={() => handleExecute(selectedNodeId)}
                  sx={{ px: 2.5, fontWeight: 600, textTransform: 'none' }}
                >
                  {loading ? 'Synthesizing...' : `Run Active: ${activeNodeName}`}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Two-Panel Layout */}
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
          {/* Left Panel: Pipeline Hierarchy Tree */}
          <Card
            variant="outlined"
            sx={{
              width: 320,
              flexShrink: 0,
              borderRadius: 2,
              maxHeight: 'calc(100vh - 220px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                p: 2,
                pb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TreeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Pipeline Hierarchy
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {isTreeSaving ? (
                  <Tooltip title="Saving changes to backend DynamoDB...">
                    <CircularProgress size={16} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Synchronized with backend DynamoDB">
                    <CloudDoneIcon color="success" sx={{ fontSize: 18 }} />
                  </Tooltip>
                )}
                <Tooltip title="Reset all processes in backend DynamoDB">
                  <IconButton size="small" onClick={handleResetTree}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {isTreeLoading && <LinearProgress color="primary" sx={{ height: 2 }} />}

            <Box sx={{ p: 1.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Root Node: Base Data */}
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  borderColor: selectedNodeId === 'base-data' ? 'primary.main' : 'divider',
                  borderWidth: selectedNodeId === 'base-data' ? 2 : 1,
                  bgcolor: selectedNodeId === 'base-data' ? 'primary.50' : 'background.paper',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => setSelectedNodeId('base-data')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="ROOT" size="small" color="primary" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} />
                    <Typography variant="body2" fontWeight={700}>
                      Base Data
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Run Base Data">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExecute('base-data');
                        }}
                      >
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Fork child process from Base Data">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFork('base-data', 'Base Data');
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Micro-sources A to F canonical bi-level synthesis.
                </Typography>
              </Paper>

              {/* Child Process Nodes */}
              {orderedTreeNodes.map(({ node, depth }) => {
                const isSelected = selectedNodeId === node.id;
                const totalFilters = (node.mainFilters?.length || 0) + (node.detailsFilters?.length || 0);
                const totalCalcs = (node.mainCalculations?.length || 0) + (node.detailsCalculations?.length || 0);

                return (
                  <Paper
                    key={node.id}
                    variant="outlined"
                    sx={{
                      ml: (depth - 1) * 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => setSelectedNodeId(node.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {node.name}
                        </Typography>
                        {node.description && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {node.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={`${totalFilters} filters`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                          <Chip
                            label={`${totalCalcs} calcs`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="Run this process">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExecute(node.id);
                            }}
                          >
                            <PlayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Fork child from here">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenFork(node.id, node.name);
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit process">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEdit(node);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloneNode(node);
                            }}
                          >
                            <CloneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNode(node.id, node.name);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Card>

          {/* Right Panel: Data Autopsy & Export */}
          <Card variant="outlined" sx={{ flexGrow: 1, borderRadius: 2, minWidth: 0 }}>
            {/* Action Bar */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  Data Autopsy
                </Typography>
                <Chip
                  label={activeNodeName}
                  color={selectedNodeId === 'base-data' ? 'primary' : 'secondary'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                {lastExecutedNodeId && (
                  <Typography variant="caption" color="text.secondary">
                    {data ? `${data.length} employees (${data.reduce((acc, e) => acc + (e.attandanceInfo?.length || 0), 0)} daily records)` : '0 records'}
                    {executionTimeMs !== null && ` • Executed in ${executionTimeMs}ms`}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search employee..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 220, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!data || data.length === 0}
                  onClick={handleExportJson}
                >
                  JSON
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CsvIcon />}
                  disabled={!data || data.length === 0}
                  onClick={handleExportCsv}
                >
                  CSV
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  disabled={!data || data.length === 0}
                  onClick={handleCopyCsv}
                >
                  Copy CSV
                </Button>
              </Box>
            </Box>

            {/* Error banner */}
            {executionError && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">{executionError}</Alert>
              </Box>
            )}

            {/* Loading Indicator */}
            {loading && <LinearProgress color="primary" />}

            {/* Empty or Loaded Table View */}
            {!data && !loading ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" fontWeight={500}>
                  No Pipeline Data Rendered
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 480, mx: 'auto' }}>
                  Select <strong>Base Data</strong> or any configured process from the left panel and click{' '}
                  <strong>Run Active</strong> to synthesize records and evaluate expressions.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 'calc(100vh - 310px)' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell sx={{ fontWeight: 700 }}>Internal ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Employee Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Joining Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Resignation Date</TableCell>
                      {dynamicMainCols.map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {col}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Daily Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecords
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((emp) => {
                        const isExpanded = !!expandedRowIds[emp.employeeUUID];
                        const dailyCount = emp.attandanceInfo?.length || 0;

                        return (
                          <React.Fragment key={emp.employeeUUID}>
                            <TableRow
                              hover
                              sx={{
                                cursor: 'pointer',
                                bgcolor: isExpanded ? 'action.hover' : 'inherit',
                              }}
                              onClick={() => toggleRowExpanded(emp.employeeUUID)}
                            >
                              <TableCell>
                                <IconButton size="small">
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{emp.internalId}</TableCell>
                              <TableCell>{emp.name}</TableCell>
                              <TableCell>{emp.joiningDate || '—'}</TableCell>
                              <TableCell>{emp.resignationDate || '—'}</TableCell>
                              {dynamicMainCols.map((col) => (
                                <TableCell key={col}>{renderCellValue(emp[col])}</TableCell>
                              ))}
                              <TableCell sx={{ textAlign: 'right' }}>
                                <Chip
                                  label={`${dailyCount} days`}
                                  size="small"
                                  color={dailyCount > 0 ? 'primary' : 'default'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.72rem' }}
                                />
                              </TableCell>
                            </TableRow>

                            {/* Subtable for Attendance Info */}
                            <TableRow>
                              <TableCell sx={{ py: 0 }} colSpan={6 + dynamicMainCols.length}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ my: 1.5, mx: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                                      Daily Attendance & Shift Details for {emp.name} ({emp.internalId})
                                    </Typography>
                                    {dailyCount === 0 ? (
                                      <Typography variant="caption" color="text.disabled">
                                        No daily attendance records retained for this employee.
                                      </Typography>
                                    ) : (
                                      <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Shift</TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>In Time</TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Out Time</TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Late (min)</TableCell>
                                              {dynamicDetailCols.map((col) => (
                                                <TableCell
                                                  key={col}
                                                  sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'secondary.main' }}
                                                >
                                                  {col}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {emp.attandanceInfo.map((day: AttendanceInfoRecord, dIdx: number) => (
                                              <TableRow key={day.Date || dIdx} hover>
                                                <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>
                                                  {day.Date}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem' }}>
                                                  {day.shiftName || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>
                                                  {day.inTime || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>
                                                  {day.outTime || '—'}
                                                </TableCell>
                                                <TableCell>
                                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {day.isLate && (
                                                      <Chip
                                                        label="Late"
                                                        size="small"
                                                        color="warning"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                      />
                                                    )}
                                                    {day.isAbsence && (
                                                      <Chip
                                                        label="Absence"
                                                        size="small"
                                                        color="error"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                      />
                                                    )}
                                                    {day.isOnLeave && (
                                                      <Chip
                                                        label={`Leave: ${day.LeaveType || ''}`}
                                                        size="small"
                                                        color="info"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                      />
                                                    )}
                                                    {day.isHoliday && (
                                                      <Chip
                                                        label="Holiday"
                                                        size="small"
                                                        color="success"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                      />
                                                    )}
                                                    {day.isWeeklyOff && (
                                                      <Chip
                                                        label="Weekly Off"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                      />
                                                    )}
                                                    {!day.isLate && !day.isAbsence && !day.isOnLeave && !day.isHoliday && !day.isWeeklyOff && (
                                                      <Chip
                                                        label="Present"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                                      />
                                                    )}
                                                  </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem' }}>
                                                  {day.LateMinute > 0 ? day.LateMinute : '—'}
                                                </TableCell>
                                                {dynamicDetailCols.map((col) => (
                                                  <TableCell key={col}>{renderCellValue(day[col])}</TableCell>
                                                ))}
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Paper>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {data && data.length > 0 && (
              <TablePagination
                component="div"
                count={filteredRecords.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Card>
        </Box>
      </Box>

      {/* Configuration Modal for Add Fork & Edit */}
      <ProcessConfigModal
        open={modalOpen}
        node={modalNode}
        parentId={modalParentId}
        parentName={modalParentName}
        onSave={handleSaveModalNode}
        onClose={() => setModalOpen(false)}
      />
    </DashboardLayout>
  );
};

export default ReportDataSource;
